'use client';

import { createClient } from '@/lib/supabase/client';
import { getPlanRationale, buildTaskRationale, type CognitiveStateLabel as SharedCognitiveStateLabel,  } from '@/lib/services/explanationGeneratorService';


// ─── Types ────────────────────────────────────────────────────────────────────

export type GameType = 'go_no_go' | 'n_back' | 'task_switching';
export type DifficultyLevel = 'easy' | 'medium' | 'hard';
export type CognitiveStateLabel = 'focused' | 'fatigued' | 'stressed' | 'recovering' | 'balanced' | 'elevated';

export interface CognitiveProfile {
  id?: string;
  userId: string;
  focusScore: number;
  mentalEnergy: number;
  stability: number;
  flexibility: number;
  reactionTimeAvg: number;
  fatigueIndex: number;
  hrvScore?: number;
  sleepScore?: number;
  eegAlpha?: number;
  eegTheta?: number;
  eegBeta?: number;
  sessionCount: number;
  streakDays: number;
  lastSessionAt?: string;
  personalBestFocus: number;
  personalBestMemory: number;
  personalBestFlexibility: number;
  plateauDetected: boolean;
  plateauDomain?: string;
  plateauSince?: string;
}

export interface GameParameters {
  difficulty: DifficultyLevel;
  stimulusIntervalMs: number;
  displayDurationMs: number;
  nBackLevel: number;
  totalTrials: number;
  distractorCount: number;
  ruleComplexity: number;
  sessionLengthSec: number;
}

export interface GamePerformanceMetrics {
  score: number;
  accuracy: number;
  reactionTimeAvg: number;
  trialsTotal: number;
  trialsCorrect: number;
  durationMs: number;
  difficultyAdjustments: number;
  fatigueDetected: boolean;
}

export interface BrainGymSession {
  id?: string;
  userId: string;
  sessionDate: string;
  gameType: GameType;
  difficultyUsed: DifficultyLevel;
  parametersUsed: GameParameters;
  performanceMetrics: GamePerformanceMetrics;
  score: number;
  accuracy: number;
  reactionTimeAvg?: number;
  trialsTotal: number;
  trialsCorrect: number;
  durationMs: number;
  cognitiveStateBefore?: CognitiveStateLabel;
  cognitiveStateAfter?: CognitiveStateLabel;
  focusBefore?: number;
  focusAfter?: number;
  mentalEnergyBefore?: number;
  mentalEnergyAfter?: number;
  stabilityBefore?: number;
  stabilityAfter?: number;
  flexibilityBefore?: number;
  flexibilityAfter?: number;
  difficultyAdjustments: number;
  fatigueDetected: boolean;
  plateauContribution: boolean;
  eegAvailable: boolean;
  hrvScore?: number;
  aiInsight?: string;
  createdAt?: string;
}

export interface TrainingTask {
  id: string;
  gameType: GameType;
  label: string;
  rationale: string;
  estimatedDurationSec: number;
  targetDomain: string;
  difficulty: DifficultyLevel;
  completed: boolean;
  sessionId?: string;
  score?: number;
}

export interface DailyTrainingPlan {
  id?: string;
  userId: string;
  planDate: string;
  cognitiveState: CognitiveStateLabel;
  stateConfidence: number;
  stateSources: string[];
  tasks: TrainingTask[];
  completedTasks: string[];
  totalTasks: number;
  completedCount: number;
  isComplete: boolean;
  planRationale: string;
  generatedFrom: string;
}

export interface ProgressSnapshot {
  snapshotDate: string;
  focusScore: number;
  mentalEnergy: number;
  stability: number;
  flexibility: number;
  overallScore: number;
  goNoGoAvg: number;
  nBackAvg: number;
  taskSwitchingAvg: number;
  sessionsThisWeek: number;
  streakDays: number;
  plateauDetected: boolean;
}

export interface PlateauAnalysis {
  detected: boolean;
  domain?: string;
  since?: string;
  recommendation?: string;
  variationSuggestion?: string;
}

// ─── Drift-Adaptive Training ──────────────────────────────────────────────────

export type DriftAdaptationType = 'reduce-intensity' | 'increase-challenge' | 'recalibrate' | 'none';

export interface DriftAdaptationResult {
  adaptationType: DriftAdaptationType;
  adjustedDifficulty: DifficultyLevel;
  rationale: string;
  triggerRecalibration: boolean;
}

/**
 * Adapt Brain Gym training plan based on drift direction and category.
 *
 * Rules:
 * - Negative drift (fatigue, slower reaction) → reduce intensity, switch to lighter tasks
 * - Positive drift (improvement) → increase challenge, introduce higher load
 * - Unexpected/unknown drift → trigger recalibration session
 */
export function adaptTrainingForDrift(
  currentDifficulty: DifficultyLevel,
  driftType: 'positive' | 'negative' | 'neutral',
  driftCategory: 'fatigue-related' | 'stress-related' | 'recovery-related' | 'performance-improvement' | 'none'
): DriftAdaptationResult {
  if (driftType === 'negative') {
    if (driftCategory === 'fatigue-related' || driftCategory === 'stress-related') {
      const reduced: DifficultyLevel = currentDifficulty === 'hard' ? 'medium' : 'easy';
      return {
        adaptationType: 'reduce-intensity',
        adjustedDifficulty: reduced,
        rationale: driftCategory === 'fatigue-related' ?'Fatigue signals detected — reducing intensity to support recovery and prevent burnout.' :'Stress signals detected — switching to lighter tasks to reduce cognitive load.',
        triggerRecalibration: false,
      };
    }
    // Negative but unknown category → recalibrate
    return {
      adaptationType: 'recalibrate',
      adjustedDifficulty: 'easy',
      rationale: 'Unexpected performance decline detected — starting a recalibration session to re-establish your baseline.',
      triggerRecalibration: true,
    };
  }

  if (driftType === 'positive') {
    if (driftCategory === 'performance-improvement' || driftCategory === 'recovery-related') {
      const increased: DifficultyLevel = currentDifficulty === 'easy' ? 'medium' : 'hard';
      return {
        adaptationType: 'increase-challenge',
        adjustedDifficulty: increased,
        rationale: driftCategory === 'performance-improvement'
          ? 'Performance improvement detected — increasing challenge to match your elevated state.' :'Recovery signals detected — gradually increasing load as your system restores.',
        triggerRecalibration: false,
      };
    }
    return {
      adaptationType: 'increase-challenge',
      adjustedDifficulty: currentDifficulty === 'easy' ? 'medium' : currentDifficulty,
      rationale: 'Positive change detected — maintaining or slightly increasing challenge.',
      triggerRecalibration: false,
    };
  }

  // Neutral drift
  return {
    adaptationType: 'none',
    adjustedDifficulty: currentDifficulty,
    rationale: 'No significant drift — maintaining current training intensity.',
    triggerRecalibration: false,
  };
}

// ─── Gradual Adaptation Constraints ──────────────────────────────────────────

export interface AdaptationConstraints {
  /** Minimum number of sessions at current difficulty before allowing change */
  minSessionsBeforeChange: number;
  /** Maximum number of difficulty steps allowed per adaptation event (1 = one level at a time) */
  maxStepsPerAdaptation: number;
  /** Minimum stability score required before increasing difficulty */
  minStabilityForIncrease: number;
  /** Whether a single drift event can trigger adaptation (false = require 2+) */
  allowSingleEventAdaptation: boolean;
}

export const DEFAULT_ADAPTATION_CONSTRAINTS: AdaptationConstraints = {
  minSessionsBeforeChange: 2,
  maxStepsPerAdaptation: 1,
  minStabilityForIncrease: 50,
  allowSingleEventAdaptation: false,
};

export interface GradualAdaptationResult {
  /** Whether adaptation was applied */
  adapted: boolean;
  /** The resulting difficulty */
  difficulty: DifficultyLevel;
  /** Why adaptation was blocked or applied */
  rationale: string;
  /** True when adaptation was blocked by constraints */
  constrained: boolean;
}

/**
 * Apply gradual adaptation constraints to a proposed difficulty change.
 *
 * Rules:
 *  - Never skip a difficulty level (easy → hard is blocked; must go easy → medium → hard)
 *  - Require minimum session stability before increasing difficulty
 *  - Single drift events do NOT trigger adaptation (require 2+ events)
 *  - Minimum sessions at current difficulty before any change
 *
 * Goal: Ensure training feels consistent and fair.
 */
export function applyAdaptationConstraints(
  currentDifficulty: DifficultyLevel,
  proposedDifficulty: DifficultyLevel,
  sessionsAtCurrentDifficulty: number,
  stabilityScore: number,
  driftEventsToday: number,
  constraints: AdaptationConstraints = DEFAULT_ADAPTATION_CONSTRAINTS
): GradualAdaptationResult {
  const difficultyOrder: DifficultyLevel[] = ['easy', 'medium', 'hard'];
  const currentIdx = difficultyOrder.indexOf(currentDifficulty);
  const proposedIdx = difficultyOrder.indexOf(proposedDifficulty);
  const stepDelta = proposedIdx - currentIdx;

  // No change proposed
  if (stepDelta === 0) {
    return {
      adapted: false,
      difficulty: currentDifficulty,
      rationale: 'No difficulty change proposed — maintaining current level.',
      constrained: false,
    };
  }

  // ── Constraint 1: Single event adaptation blocked ──
  if (!constraints.allowSingleEventAdaptation && driftEventsToday <= 1) {
    return {
      adapted: false,
      difficulty: currentDifficulty,
      rationale: 'Single drift event — maintaining current difficulty to avoid over-reacting. Requires 2+ events for adaptation.',
      constrained: true,
    };
  }

  // ── Constraint 2: Minimum sessions before change ──
  if (sessionsAtCurrentDifficulty < constraints.minSessionsBeforeChange) {
    return {
      adapted: false,
      difficulty: currentDifficulty,
      rationale: `Minimum session stability not met (${sessionsAtCurrentDifficulty}/${constraints.minSessionsBeforeChange} sessions at current difficulty). Maintaining level for consistency.`,
      constrained: true,
    };
  }

  // ── Constraint 3: Stability required for increase ──
  if (stepDelta > 0 && stabilityScore < constraints.minStabilityForIncrease) {
    return {
      adapted: false,
      difficulty: currentDifficulty,
      rationale: `Stability score too low (${stabilityScore}/100) to increase difficulty. Requires ≥${constraints.minStabilityForIncrease} for upward adaptation.`,
      constrained: true,
    };
  }

  // ── Constraint 4: Maximum one step per adaptation ──
  const clampedDelta = Math.sign(stepDelta) * Math.min(Math.abs(stepDelta), constraints.maxStepsPerAdaptation);
  const clampedIdx = Math.max(0, Math.min(2, currentIdx + clampedDelta));
  const clampedDifficulty = difficultyOrder[clampedIdx];

  const wasSkipped = clampedDifficulty !== proposedDifficulty;

  return {
    adapted: clampedDifficulty !== currentDifficulty,
    difficulty: clampedDifficulty,
    rationale: wasSkipped
      ? `Gradual adjustment applied — stepped from ${currentDifficulty} to ${clampedDifficulty} (skipping ${proposedDifficulty} to avoid abrupt change).`
      : `Difficulty adjusted from ${currentDifficulty} to ${clampedDifficulty} based on sustained drift signals.`,
    constrained: wasSkipped,
  };
}

/**
 * Apply drift adaptation to a generated daily training plan WITH gradual constraints.
 * Replaces the unconstrained version — prevents over-reacting to single drift events.
 */
export function applyDriftAdaptationToPlan(
  plan: DailyTrainingPlan,
  driftType: 'positive' | 'negative' | 'neutral',
  driftCategory: 'fatigue-related' | 'stress-related' | 'recovery-related' | 'performance-improvement' | 'none',
  driftReason: string,
  stabilityScore = 50,
  driftEventsToday = 1,
  sessionsAtCurrentDifficulty = 0
): DailyTrainingPlan & { driftAdaptation?: DriftAdaptationResult } {
  if (driftType === 'neutral' && driftCategory === 'none') {
    return plan;
  }

  const currentDifficulty = plan.tasks[0]?.difficulty ?? 'medium';
  const rawAdaptation = adaptTrainingForDrift(currentDifficulty, driftType, driftCategory);

  if (rawAdaptation.adaptationType === 'none') {
    return plan;
  }

  // ── Apply gradual constraints ──
  const constraintResult = applyAdaptationConstraints(
    currentDifficulty,
    rawAdaptation.adjustedDifficulty,
    sessionsAtCurrentDifficulty,
    stabilityScore,
    driftEventsToday
  );

  const finalDifficulty = constraintResult.constrained
    ? constraintResult.difficulty
    : rawAdaptation.adjustedDifficulty;

  const finalRationale = constraintResult.constrained
    ? constraintResult.rationale
    : rawAdaptation.rationale;

  const adaptation: DriftAdaptationResult = {
    ...rawAdaptation,
    adjustedDifficulty: finalDifficulty,
    rationale: finalRationale,
  };

  if (finalDifficulty === currentDifficulty && !rawAdaptation.triggerRecalibration) {
    return { ...plan, driftAdaptation: adaptation };
  }

  const adaptedTasks = plan.tasks.map(task => ({
    ...task,
    difficulty: finalDifficulty,
    rationale: `${task.rationale} [Drift-adjusted: ${finalRationale}]`,
    estimatedDurationSec: finalDifficulty === 'easy' ? 60 : finalDifficulty === 'medium' ? 90 : 120,
  }));

  return {
    ...plan,
    tasks: adaptedTasks,
    planRationale: `${plan.planRationale} — ${finalRationale}`,
    driftAdaptation: adaptation,
  };
}

// ─── Default Parameters per Difficulty ───────────────────────────────────────

export const DEFAULT_PARAMS: Record<DifficultyLevel, GameParameters> = {
  easy: {
    difficulty: 'easy',
    stimulusIntervalMs: 2500,
    displayDurationMs: 1800,
    nBackLevel: 1,
    totalTrials: 12,
    distractorCount: 3,
    ruleComplexity: 1,
    sessionLengthSec: 60,
  },
  medium: {
    difficulty: 'medium',
    stimulusIntervalMs: 1800,
    displayDurationMs: 1400,
    nBackLevel: 2,
    totalTrials: 18,
    distractorCount: 5,
    ruleComplexity: 2,
    sessionLengthSec: 90,
  },
  hard: {
    difficulty: 'hard',
    stimulusIntervalMs: 1200,
    displayDurationMs: 900,
    nBackLevel: 3,
    totalTrials: 24,
    distractorCount: 8,
    ruleComplexity: 3,
    sessionLengthSec: 120,
  },
};

// ─── Adaptive Difficulty Engine ───────────────────────────────────────────────

export function computeAdaptiveDifficulty(
  profile: CognitiveProfile,
  recentScores: number[],
  currentDifficulty: DifficultyLevel,
  fatigueDetected: boolean
): DifficultyLevel {
  if (fatigueDetected) {
    return currentDifficulty === 'hard' ? 'medium' : 'easy';
  }

  if (recentScores.length < 2) return currentDifficulty;

  const avgScore = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
  const trend = recentScores.length >= 3
    ? recentScores[recentScores.length - 1] - recentScores[0]
    : 0;

  // Increase difficulty: consistently high performance
  if (avgScore >= 80 && trend >= 0 && currentDifficulty !== 'hard') {
    return currentDifficulty === 'easy' ? 'medium' : 'hard';
  }

  // Decrease difficulty: struggling
  if (avgScore < 45 && currentDifficulty !== 'easy') {
    return currentDifficulty === 'hard' ? 'medium' : 'easy';
  }

  return currentDifficulty;
}

export function adaptParametersRealTime(
  params: GameParameters,
  runningAccuracy: number,
  runningReactionMs: number,
  trialCount: number
): GameParameters {
  if (trialCount < 5) return params; // Not enough data yet

  const adapted = { ...params };

  // Performing well → increase speed/complexity
  if (runningAccuracy > 85 && runningReactionMs < 500) {
    adapted.stimulusIntervalMs = Math.max(800, params.stimulusIntervalMs - 100);
    adapted.displayDurationMs = Math.max(600, params.displayDurationMs - 100);
  }

  // Struggling → slow down
  if (runningAccuracy < 50 || runningReactionMs > 1500) {
    adapted.stimulusIntervalMs = Math.min(3000, params.stimulusIntervalMs + 200);
    adapted.displayDurationMs = Math.min(2500, params.displayDurationMs + 200);
  }

  return adapted;
}

export function detectFatigue(
  reactionTimes: number[],
  accuracyHistory: number[]
): boolean {
  if (reactionTimes.length < 8) return false;

  // Check for increasing RT trend in last 8 trials
  const recent = reactionTimes.slice(-8);
  const firstHalf = recent.slice(0, 4).reduce((a, b) => a + b, 0) / 4;
  const secondHalf = recent.slice(4).reduce((a, b) => a + b, 0) / 4;
  const rtIncrease = (secondHalf - firstHalf) / firstHalf;

  // Check accuracy drop
  let accuracyDrop = false;
  if (accuracyHistory.length >= 6) {
    const recentAcc = accuracyHistory.slice(-6);
    const firstAcc = recentAcc.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
    const lastAcc = recentAcc.slice(3).reduce((a, b) => a + b, 0) / 3;
    accuracyDrop = lastAcc < firstAcc - 15;
  }

  return rtIncrease > 0.25 || accuracyDrop;
}

// ─── Plateau Detection ────────────────────────────────────────────────────────

export function detectPlateau(
  sessionHistory: { gameType: GameType; score: number; createdAt: string }[],
  domain: GameType
): PlateauAnalysis {
  const domainSessions = sessionHistory
    .filter(s => s.gameType === domain)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .slice(-10);

  if (domainSessions.length < 5) {
    return { detected: false };
  }

  const scores = domainSessions.map(s => s.score);
  const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
  const variance = scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / scores.length;
  const stdDev = Math.sqrt(variance);

  // Plateau: low variance AND no improvement trend
  const firstHalf = scores.slice(0, Math.floor(scores.length / 2));
  const secondHalf = scores.slice(Math.floor(scores.length / 2));
  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
  const improvement = secondAvg - firstAvg;

  const isPlateaued = stdDev < 8 && Math.abs(improvement) < 5;

  if (!isPlateaued) return { detected: false };

  const domainLabels: Record<GameType, string> = {
    go_no_go: 'Attention & Inhibition',
    n_back: 'Working Memory',
    task_switching: 'Cognitive Flexibility',
  };

  const variations: Record<GameType, string> = {
    go_no_go: 'Switch to auditory stimuli or increase distractor complexity',
    n_back: 'Increase N-back level or introduce dual-task variation',
    task_switching: 'Add a third rule dimension or reduce inter-trial interval',
  };

  return {
    detected: true,
    domain: domainLabels[domain],
    since: domainSessions[domainSessions.length - 5]?.createdAt,
    recommendation: `Plateau detected in ${domainLabels[domain]}. Introducing variation to re-stimulate neural adaptation.`,
    variationSuggestion: variations[domain],
  };
}

// ─── Daily Plan Generator ─────────────────────────────────────────────────────

export function generateDailyPlan(
  profile: CognitiveProfile,
  cognitiveState: CognitiveStateLabel,
  stateConfidence: number,
  stateSources: string[],
  recentSessions: BrainGymSession[],
  plateauAnalysis: PlateauAnalysis
): DailyTrainingPlan {
  const today = new Date().toISOString().split('T')[0];

  // Determine difficulty based on state
  let baseDifficulty: DifficultyLevel = 'medium';
  if (cognitiveState === 'fatigued' || cognitiveState === 'stressed') {
    baseDifficulty = 'easy';
  } else if (cognitiveState === 'focused' || cognitiveState === 'elevated') {
    baseDifficulty = 'hard';
  }

  // Find weakest domains
  const domainScores: Record<GameType, number> = {
    go_no_go: profile.focusScore,
    n_back: profile.mentalEnergy,
    task_switching: profile.flexibility,
  };

  // Sort by weakest first (prioritise training)
  const sortedDomains = (Object.entries(domainScores) as [GameType, number][])
    .sort((a, b) => a[1] - b[1]);

  // If plateau detected, swap that domain's difficulty
  const tasks: TrainingTask[] = sortedDomains.map(([gameType, score], idx) => {
    let difficulty = baseDifficulty;

    const isPlateauDomain = !!(
      plateauAnalysis.detected &&
      plateauAnalysis.domain?.toLowerCase().includes(gameType.replace('_', ' '))
    );

    if (isPlateauDomain) {
      difficulty = 'hard';
    } else if (score < 45) {
      difficulty = 'easy';
    } else if (score > 75) {
      difficulty = difficulty === 'easy' ? 'medium' : 'hard';
    }

    // Use shared rationale builder for consistent wording
    const rationale = buildTaskRationale(score, difficulty, isPlateauDomain);

    const labels: Record<GameType, string> = {
      go_no_go: 'Attention & Inhibition',
      n_back: 'Working Memory',
      task_switching: 'Cognitive Flexibility',
    };

    const durations: Record<DifficultyLevel, number> = { easy: 60, medium: 90, hard: 120 };

    return {
      id: `task_${gameType}_${today}`,
      gameType,
      label: labels[gameType],
      rationale,
      estimatedDurationSec: durations[difficulty],
      targetDomain: labels[gameType],
      difficulty,
      completed: false,
    };
  });

  return {
    userId: profile.userId,
    planDate: today,
    cognitiveState,
    stateConfidence,
    stateSources,
    tasks,
    completedTasks: [],
    totalTasks: tasks.length,
    completedCount: 0,
    isComplete: false,
    // Use shared plan rationale for consistent wording
    planRationale: getPlanRationale(cognitiveState),
    generatedFrom: stateSources[0] || 'brain_check',
  };
}

// ─── Profile Update Engine ────────────────────────────────────────────────────

export function updateProfileFromSession(
  profile: CognitiveProfile,
  session: BrainGymSession
): Partial<CognitiveProfile> {
  const alpha = 0.2; // EMA smoothing factor

  const updates: Partial<CognitiveProfile> = {
    sessionCount: profile.sessionCount + 1,
    lastSessionAt: new Date().toISOString(),
  };

  // Update domain scores via EMA
  if (session.gameType === 'go_no_go') {
    updates.focusScore = profile.focusScore * (1 - alpha) + session.score * alpha;
    updates.personalBestFocus = Math.max(profile.personalBestFocus, session.score);
  } else if (session.gameType === 'n_back') {
    updates.mentalEnergy = profile.mentalEnergy * (1 - alpha) + session.score * alpha;
    updates.personalBestMemory = Math.max(profile.personalBestMemory, session.score);
  } else if (session.gameType === 'task_switching') {
    updates.flexibility = profile.flexibility * (1 - alpha) + session.score * alpha;
    updates.personalBestFlexibility = Math.max(profile.personalBestFlexibility, session.score);
  }

  // Update reaction time
  if (session.reactionTimeAvg) {
    updates.reactionTimeAvg = profile.reactionTimeAvg * (1 - alpha) + session.reactionTimeAvg * alpha;
  }

  // Update fatigue index
  updates.fatigueIndex = session.fatigueDetected
    ? Math.min(100, profile.fatigueIndex + 15)
    : Math.max(0, profile.fatigueIndex - 10);

  // Update stability (based on score variance)
  const scoreDiff = Math.abs(session.score - profile.focusScore);
  updates.stability = profile.stability * (1 - alpha) + Math.max(0, 100 - scoreDiff * 2) * alpha;

  return updates;
}

// ─── Cognitive Fingerprint ────────────────────────────────────────────────────

export interface CognitiveFingerprint {
  dominantStrength: string;
  dominantWeakness: string;
  trainingStyle: string;
  adaptationRate: string;
  consistencyRating: string;
  uniquePattern: string;
}

export function computeCognitiveFingerprint(profile: CognitiveProfile): CognitiveFingerprint {
  const scores = {
    'Attention': profile.focusScore,
    'Memory': profile.mentalEnergy,
    'Flexibility': profile.flexibility,
    'Stability': profile.stability,
  };

  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const dominantStrength = sorted[0][0];
  const dominantWeakness = sorted[sorted.length - 1][0];

  const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / 4;
  const trainingStyle = avgScore > 70 ? 'High-Performance Optimizer'
    : avgScore > 55 ? 'Balanced Developer'
    : avgScore > 40 ? 'Foundational Builder' :'Recovery-Focused Rehabilitator';

  const adaptationRate = profile.sessionCount > 20 ? 'Rapid Adapter'
    : profile.sessionCount > 10 ? 'Steady Learner' :'Early Stage';

  const variance = Object.values(scores).reduce((a, b) => a + Math.pow(b - avgScore, 2), 0) / 4;
  const consistencyRating = Math.sqrt(variance) < 10 ? 'Highly Consistent'
    : Math.sqrt(variance) < 20 ? 'Moderately Variable' :'Domain-Specialist';

  const uniquePattern = `${dominantStrength}-dominant, ${dominantWeakness}-developing profile with ${adaptationRate.toLowerCase()} characteristics`;

  return {
    dominantStrength,
    dominantWeakness,
    trainingStyle,
    adaptationRate,
    consistencyRating,
    uniquePattern,
  };
}

// ─── Supabase Operations ──────────────────────────────────────────────────────

const supabase = createClient();

export async function getCognitiveProfile(userId: string): Promise<CognitiveProfile | null> {
  const { data, error } = await supabase
    .from('cognitive_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    userId: data.user_id,
    focusScore: Number(data.focus_score),
    mentalEnergy: Number(data.mental_energy),
    stability: Number(data.stability),
    flexibility: Number(data.flexibility),
    reactionTimeAvg: Number(data.reaction_time_avg),
    fatigueIndex: Number(data.fatigue_index),
    hrvScore: data.hrv_score ? Number(data.hrv_score) : undefined,
    sleepScore: data.sleep_score ? Number(data.sleep_score) : undefined,
    eegAlpha: data.eeg_alpha ? Number(data.eeg_alpha) : undefined,
    eegTheta: data.eeg_theta ? Number(data.eeg_theta) : undefined,
    eegBeta: data.eeg_beta ? Number(data.eeg_beta) : undefined,
    sessionCount: data.session_count,
    streakDays: data.streak_days,
    lastSessionAt: data.last_session_at,
    personalBestFocus: Number(data.personal_best_focus),
    personalBestMemory: Number(data.personal_best_memory),
    personalBestFlexibility: Number(data.personal_best_flexibility),
    plateauDetected: data.plateau_detected,
    plateauDomain: data.plateau_domain,
    plateauSince: data.plateau_since,
  };
}

export async function upsertCognitiveProfile(profile: Partial<CognitiveProfile> & { userId: string }): Promise<void> {
  await supabase.from('cognitive_profiles').upsert({
    user_id: profile.userId,
    focus_score: profile.focusScore,
    mental_energy: profile.mentalEnergy,
    stability: profile.stability,
    flexibility: profile.flexibility,
    reaction_time_avg: profile.reactionTimeAvg,
    fatigue_index: profile.fatigueIndex,
    hrv_score: profile.hrvScore,
    sleep_score: profile.sleepScore,
    eeg_alpha: profile.eegAlpha,
    eeg_theta: profile.eegTheta,
    eeg_beta: profile.eegBeta,
    session_count: profile.sessionCount,
    streak_days: profile.streakDays,
    last_session_at: profile.lastSessionAt,
    personal_best_focus: profile.personalBestFocus,
    personal_best_memory: profile.personalBestMemory,
    personal_best_flexibility: profile.personalBestFlexibility,
    plateau_detected: profile.plateauDetected,
    plateau_domain: profile.plateauDomain,
    plateau_since: profile.plateauSince,
  }, { onConflict: 'user_id' });
}

export async function saveBrainGymSession(session: BrainGymSession): Promise<string | null> {
  const { data, error } = await supabase.from('brain_gym_sessions').insert({
    user_id: session.userId,
    session_date: session.sessionDate,
    game_type: session.gameType,
    difficulty_used: session.difficultyUsed,
    parameters_used: session.parametersUsed,
    performance_metrics: session.performanceMetrics,
    score: session.score,
    accuracy: session.accuracy,
    reaction_time_avg: session.reactionTimeAvg,
    trials_total: session.trialsTotal,
    trials_correct: session.trialsCorrect,
    duration_ms: session.durationMs,
    cognitive_state_before: session.cognitiveStateBefore,
    cognitive_state_after: session.cognitiveStateAfter,
    focus_before: session.focusBefore,
    focus_after: session.focusAfter,
    mental_energy_before: session.mentalEnergyBefore,
    mental_energy_after: session.mentalEnergyAfter,
    stability_before: session.stabilityBefore,
    stability_after: session.stabilityAfter,
    flexibility_before: session.flexibilityBefore,
    flexibility_after: session.flexibilityAfter,
    difficulty_adjustments: session.difficultyAdjustments,
    fatigue_detected: session.fatigueDetected,
    plateau_contribution: session.plateauContribution,
    eeg_available: session.eegAvailable,
    hrv_score: session.hrvScore,
    ai_insight: session.aiInsight,
  }).select('id').single();

  if (error) return null;
  return data?.id ?? null;
}

export async function getRecentGymSessions(userId: string, limit = 30): Promise<BrainGymSession[]> {
  const { data, error } = await supabase
    .from('brain_gym_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error || !data) return [];

  return data.map(d => ({
    id: d.id,
    userId: d.user_id,
    sessionDate: d.session_date,
    gameType: d.game_type as GameType,
    difficultyUsed: d.difficulty_used as DifficultyLevel,
    parametersUsed: d.parameters_used as GameParameters,
    performanceMetrics: d.performance_metrics as GamePerformanceMetrics,
    score: Number(d.score),
    accuracy: Number(d.accuracy),
    reactionTimeAvg: d.reaction_time_avg ? Number(d.reaction_time_avg) : undefined,
    trialsTotal: d.trials_total,
    trialsCorrect: d.trials_correct,
    durationMs: d.duration_ms,
    cognitiveStateBefore: d.cognitive_state_before as CognitiveStateLabel,
    cognitiveStateAfter: d.cognitive_state_after as CognitiveStateLabel,
    focusBefore: d.focus_before ? Number(d.focus_before) : undefined,
    focusAfter: d.focus_after ? Number(d.focus_after) : undefined,
    mentalEnergyBefore: d.mental_energy_before ? Number(d.mental_energy_before) : undefined,
    mentalEnergyAfter: d.mental_energy_after ? Number(d.mental_energy_after) : undefined,
    stabilityBefore: d.stability_before ? Number(d.stability_before) : undefined,
    stabilityAfter: d.stability_after ? Number(d.stability_after) : undefined,
    flexibilityBefore: d.flexibility_before ? Number(d.flexibility_before) : undefined,
    flexibilityAfter: d.flexibility_after ? Number(d.flexibility_after) : undefined,
    difficultyAdjustments: d.difficulty_adjustments,
    fatigueDetected: d.fatigue_detected,
    plateauContribution: d.plateau_contribution,
    eegAvailable: d.eeg_available,
    hrvScore: d.hrv_score ? Number(d.hrv_score) : undefined,
    aiInsight: d.ai_insight,
    createdAt: d.created_at,
  }));
}

export async function getTodayPlan(userId: string): Promise<DailyTrainingPlan | null> {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('daily_training_plans')
    .select('*')
    .eq('user_id', userId)
    .eq('plan_date', today)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    userId: data.user_id,
    planDate: data.plan_date,
    cognitiveState: data.cognitive_state as CognitiveStateLabel,
    stateConfidence: Number(data.state_confidence),
    stateSources: data.state_sources || [],
    tasks: data.tasks as TrainingTask[],
    completedTasks: data.completed_tasks || [],
    totalTasks: data.total_tasks,
    completedCount: data.completed_count,
    isComplete: data.is_complete,
    planRationale: data.plan_rationale,
    generatedFrom: data.generated_from,
  };
}

export async function saveDailyPlan(plan: DailyTrainingPlan): Promise<string | null> {
  const { data, error } = await supabase.from('daily_training_plans').upsert({
    user_id: plan.userId,
    plan_date: plan.planDate,
    cognitive_state: plan.cognitiveState,
    state_confidence: plan.stateConfidence,
    state_sources: plan.stateSources,
    tasks: plan.tasks,
    completed_tasks: plan.completedTasks,
    total_tasks: plan.totalTasks,
    completed_count: plan.completedCount,
    is_complete: plan.isComplete,
    plan_rationale: plan.planRationale,
    generated_from: plan.generatedFrom,
  }, { onConflict: 'user_id,plan_date' }).select('id').single();

  if (error) return null;
  return data?.id ?? null;
}

export async function markTaskComplete(
  planId: string,
  taskId: string,
  sessionId: string,
  score: number
): Promise<void> {
  const { data } = await supabase
    .from('daily_training_plans')
    .select('tasks, completed_tasks, completed_count, total_tasks')
    .eq('id', planId)
    .single();

  if (!data) return;

  const tasks = (data.tasks as TrainingTask[]).map(t =>
    t.id === taskId ? { ...t, completed: true, sessionId, score } : t
  );
  const completedTasks = [...(data.completed_tasks || []), taskId];
  const completedCount = completedTasks.length;
  const isComplete = completedCount >= data.total_tasks;

  await supabase.from('daily_training_plans').update({
    tasks,
    completed_tasks: completedTasks,
    completed_count: completedCount,
    is_complete: isComplete,
  }).eq('id', planId);
}

export async function getProgressSnapshots(userId: string, days = 30): Promise<ProgressSnapshot[]> {
  const { data, error } = await supabase
    .from('gym_progress_snapshots')
    .select('*')
    .eq('user_id', userId)
    .order('snapshot_date', { ascending: true })
    .limit(days);

  if (error || !data) return [];

  return data.map(d => ({
    snapshotDate: d.snapshot_date,
    focusScore: Number(d.focus_score || 0),
    mentalEnergy: Number(d.mental_energy || 0),
    stability: Number(d.stability || 0),
    flexibility: Number(d.flexibility || 0),
    overallScore: Number(d.overall_score || 0),
    goNoGoAvg: Number(d.go_no_go_avg || 0),
    nBackAvg: Number(d.n_back_avg || 0),
    taskSwitchingAvg: Number(d.task_switching_avg || 0),
    sessionsThisWeek: d.sessions_this_week,
    streakDays: d.streak_days,
    plateauDetected: d.plateau_detected,
  }));
}

export async function saveProgressSnapshot(userId: string, profile: CognitiveProfile, sessions: BrainGymSession[]): Promise<void> {
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const weekSessions = sessions.filter(s => s.createdAt && s.createdAt > weekAgo);

  const byType = (type: GameType) => {
    const s = sessions.filter(x => x.gameType === type).slice(0, 5);
    return s.length ? s.reduce((a, b) => a + b.score, 0) / s.length : 0;
  };

  await supabase.from('gym_progress_snapshots').upsert({
    user_id: userId,
    snapshot_date: today,
    focus_score: profile.focusScore,
    mental_energy: profile.mentalEnergy,
    stability: profile.stability,
    flexibility: profile.flexibility,
    overall_score: (profile.focusScore + profile.mentalEnergy + profile.stability + profile.flexibility) / 4,
    go_no_go_avg: byType('go_no_go'),
    n_back_avg: byType('n_back'),
    task_switching_avg: byType('task_switching'),
    sessions_this_week: weekSessions.length,
    streak_days: profile.streakDays,
    plateau_detected: profile.plateauDetected,
  }, { onConflict: 'user_id,snapshot_date' });
}

// ─── Default Profile ──────────────────────────────────────────────────────────

export function createDefaultProfile(userId: string): CognitiveProfile {
  return {
    userId,
    focusScore: 50,
    mentalEnergy: 50,
    stability: 50,
    flexibility: 50,
    reactionTimeAvg: 600,
    fatigueIndex: 0,
    sessionCount: 0,
    streakDays: 0,
    personalBestFocus: 0,
    personalBestMemory: 0,
    personalBestFlexibility: 0,
    plateauDetected: false,
  };
}
